import { useState } from 'react';
import type { CaptureDateFilter, CaptureStatus } from '../../data/database.types';
import type { CaptureLocationOption } from '../../data/queries/use-capture-locations';
import {
  CAPTURE_DATE_LABEL,
  CAPTURE_DATE_ORDER,
  CAPTURE_FILTER_ALL_LABEL,
  CAPTURE_FILTER_RESTING_LABEL,
  CAPTURE_STATUS_FILTER_ORDER,
  captureStatusFilterLabel,
  hasActiveCaptureFilters,
  NO_CAPTURE_FILTERS,
  type CaptureFilters,
} from './capture-filters';

/**
 * LOCATION · DATE · STATUS — M8 / S08-02. Design export S19.
 *
 * **EXACTLY THREE CHIPS, AND NO SEARCH BOX.** `PRODUCT_TRUTH` §5 states the
 * three and then says "Do not invent additional primary navigation"; the design
 * draws the same three and no text input. The Search box is S08-03 and is
 * deliberately absent here.
 *
 * **THIS COMPONENT DECIDES NOTHING ABOUT WHICH CAPTURES MATCH.** It reports a
 * selection upward and renders what it is given. The narrowing happens in
 * `search_captures`, in the database — including the Date boundaries, which are
 * resolved in the canonical Crewlink timezone and never computed here
 * (INV-T17).
 *
 * One list is open at a time. Choosing an option closes it, because a filter
 * row that stays open on a phone covers the results it just changed.
 */

type OpenChip = 'location' | 'date' | 'status' | null;

export function CaptureFilterBar({
  filters,
  draftQuery,
  locations,
  count,
  onChange,
  onQueryChange,
  onClearQuery,
}: {
  readonly filters: CaptureFilters;
  /**
   * What is IN THE BOX this instant, which runs ahead of `filters.query` while
   * the debounce settles. The box must never lag behind the keyboard.
   */
  readonly draftQuery: string;
  /** What this Project actually contains. Never invented, never hardcoded. */
  readonly locations: readonly CaptureLocationOption[];
  /** How many Captures are on screen right now, or null while unknown. */
  readonly count: number | null;
  readonly onChange: (next: CaptureFilters) => void;
  readonly onQueryChange: (next: string) => void;
  /** Clears the SEARCH TEXT ONLY. The three chips are left exactly as they are. */
  readonly onClearQuery: () => void;
}) {
  const [open, setOpen] = useState<OpenChip>(null);

  const active = hasActiveCaptureFilters(filters);
  const locationLabel =
    locations.find((option) => option.key === filters.locationKey)?.label ?? null;

  const choose = (next: CaptureFilters): void => {
    setOpen(null);
    onChange(next);
  };

  const toggle = (chip: Exclude<OpenChip, null>): void => {
    setOpen((current) => (current === chip ? null : chip));
  };

  /** A chip reads its SELECTION when it has one, and its own name when it does not. */
  const chip = (
    key: Exclude<OpenChip, null>,
    label: string,
    isActive: boolean,
  ): React.ReactElement => (
    <button
      type="button"
      className={`capture-filter-chip${isActive ? ' capture-filter-chip--active' : ''}`}
      aria-expanded={open === key}
      aria-haspopup="listbox"
      onClick={() => {
        toggle(key);
      }}
    >
      {label} ▾
    </button>
  );

  return (
    <div className="capture-filters">
      <div className="capture-filters-row">
        <h2 id="captures-heading" className="projects-group-heading capture-filters-heading">
          CAPTURES · {count === null ? '—' : count}
        </h2>
        {/* Clear appears ONLY when something is narrowed. A permanently visible
            Clear on an unfiltered list implies the list is hiding something. */}
        {active ? (
          <button
            type="button"
            className="capture-filter-clear"
            onClick={() => {
              choose(NO_CAPTURE_FILTERS);
            }}
          >
            Clear
          </button>
        ) : null}
      </div>

      {/* SEARCH RUNS AS HE TYPES. There is no Enter to press: the chips beside
          it already react to a single tap, and a Search box needing a second
          deliberate action would feel like a different product. The debounce
          lives on the screen above, not here. */}
      <div className="capture-search">
        <input
          id="capture-search"
          className="capture-search-input"
          type="text"
          // NOT type="search" — the browser's own clear affordance is
          // inconsistent across platforms and invisible to the tests. The × is
          // rendered here so its behaviour is ours to define and to prove.
          value={draftQuery}
          onChange={(event) => {
            onQueryChange(event.target.value);
          }}
          placeholder="Search Captures"
          aria-label="Search Captures"
          autoComplete="off"
        />
        {draftQuery !== '' ? (
          <button
            type="button"
            className="capture-search-clear"
            // **CLEARS THE SEARCH TEXT ONLY.** The chips are a separate
            // decision and clearing one must not silently clear the other.
            aria-label="Clear search"
            onClick={() => {
              setOpen(null);
              onClearQuery();
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      <div className="capture-filter-chips">
        {chip(
          'location',
          locationLabel ?? CAPTURE_FILTER_RESTING_LABEL.location,
          filters.locationKey !== null,
        )}
        {chip(
          'date',
          filters.date === null
            ? CAPTURE_FILTER_RESTING_LABEL.date
            : CAPTURE_DATE_LABEL[filters.date],
          filters.date !== null,
        )}
        {chip(
          'status',
          filters.status === null
            ? CAPTURE_FILTER_RESTING_LABEL.status
            : captureStatusFilterLabel(filters.status),
          filters.status !== null,
        )}
      </div>

      {open === 'location' ? (
        <ul className="capture-filter-options" role="listbox" aria-label="Location">
          <FilterOption
            label={CAPTURE_FILTER_ALL_LABEL.location}
            selected={filters.locationKey === null}
            onSelect={() => {
              choose({ ...filters, locationKey: null });
            }}
          />
          {/* Built from what this Project holds — deduplicated by the
              normalized key, and shown as Brandon's own words. The key itself
              never reaches the screen. */}
          {locations.map((option) => (
            <FilterOption
              key={option.key}
              label={option.label}
              selected={filters.locationKey === option.key}
              onSelect={() => {
                choose({ ...filters, locationKey: option.key });
              }}
            />
          ))}
          {locations.length === 0 ? (
            <li className="capture-filter-empty">No Locations in this Project yet.</li>
          ) : null}
        </ul>
      ) : null}

      {open === 'date' ? (
        <ul className="capture-filter-options" role="listbox" aria-label="Date">
          <FilterOption
            label={CAPTURE_FILTER_ALL_LABEL.date}
            selected={filters.date === null}
            onSelect={() => {
              choose({ ...filters, date: null });
            }}
          />
          {CAPTURE_DATE_ORDER.map((value: CaptureDateFilter) => (
            <FilterOption
              key={value}
              label={CAPTURE_DATE_LABEL[value]}
              selected={filters.date === value}
              onSelect={() => {
                choose({ ...filters, date: value });
              }}
            />
          ))}
        </ul>
      ) : null}

      {open === 'status' ? (
        <ul className="capture-filter-options" role="listbox" aria-label="Status">
          <FilterOption
            label={CAPTURE_FILTER_ALL_LABEL.status}
            selected={filters.status === null}
            onSelect={() => {
              choose({ ...filters, status: null });
            }}
          />
          {CAPTURE_STATUS_FILTER_ORDER.map((value: CaptureStatus) => (
            <FilterOption
              key={value}
              label={captureStatusFilterLabel(value)}
              selected={filters.status === value}
              onSelect={() => {
                choose({ ...filters, status: value });
              }}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FilterOption({
  label,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        className={`capture-filter-option${selected ? ' capture-filter-option--selected' : ''}`}
        onClick={onSelect}
      >
        {label}
      </button>
    </li>
  );
}
